import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { cn } from '@/components/ui/cn';

export type ConsultantSource =
  | 'home'
  | 'quiz_result'
  | 'quiz_result_passport'
  | 'quiz_result_buyer'
  | 'recommendations'
  | 'compare_empty'
  | 'compare_active'
  | 'next_steps'
  | 'country_hub'
  | `topic_${string}`
  | `checklist_${string}`;

export interface ConsultantCalloutProps {
  /** Where the user is when seeing this — included in CTA query string for funnel attribution. */
  source: ConsultantSource;
  /** `card` (default, boxed) or `inline` (slim banner). */
  variant?: 'card' | 'inline';
  className?: string;
  /** ISO-3166-1 alpha-2 country code — passed as ?country= to prefill the form. */
  country?: string;
  /** Topic slug — passed as ?specialty= to prefill the category. */
  specialty?: string;
}

/**
 * Reusable "Talk to a relocation consultant" CTA. Earlier the consultant
 * intake page was only linked from the NextStepsPage — every other funnel
 * surface was a dead end if the user wanted human help. This component
 * surfaces it consistently with copy tuned to the surface (a user looking
 * at quiz results has different doubts than one stuck on the compare page).
 *
 * `source` is appended as `?from=<surface>` so the consultant intake +
 * downstream analytics can attribute conversions by entry point.
 */
export function ConsultantCallout({ source, variant = 'card', className, country, specialty }: ConsultantCalloutProps) {
  const { t } = useTranslation();
  const titleKey = `consultantCta.${source}.title`;
  const bodyKey = `consultantCta.${source}.body`;
  // i18n with sensible English fallbacks so the component never renders
  // raw keys even before translations are added.
  const DEFAULTS: Record<string, { title: string; body: string }> = {
    home: {
      title: 'Want a human to walk you through this?',
      body: 'Book a 30-min call with a relocation expert. Country choice, visa pathway, cost realism — talk it through.',
    },
    quiz_result: {
      title: 'Need a second opinion on these matches?',
      body: 'A relocation consultant can sanity-check the top-3 against your specific situation — taxes, family, work.',
    },
    quiz_result_passport: {
      title: 'Passport friction? Talk to a specialist.',
      body: 'Immigration lawyers experienced with your passport type can navigate banking closures and visa restrictions.',
    },
    quiz_result_buyer: {
      title: 'Buying property? Get a real estate lawyer.',
      body: 'Property purchase abroad involves notary, tax registration, and transfer costs — get someone who has done it.',
    },
    recommendations: {
      title: 'Stuck between regions?',
      body: 'Talk to a consultant who knows Italy on the ground — they will short-list 2–3 for a visit plan.',
    },
    compare_empty: {
      title: 'Skip the comparison work?',
      body: 'A consultant can present a comparison table tailored to your budget, climate, and family in one session.',
    },
    compare_active: {
      title: 'Pick the right place to actually visit',
      body: 'A relocation consultant can sequence visits + flag the move-in details for the comuni you are comparing.',
    },
    next_steps: {
      title: 'Want a person owning these steps with you?',
      body: 'Book a consultant and we will turn this checklist into a dated plan.',
    },
    country_hub: {
      title: 'Ready to talk to a country expert?',
      body: 'A relocation consultant can answer the specific questions no article can — your timeline, costs, and first steps.',
    },
    topic_visa: {
      title: 'Ready to apply? Talk to an immigration lawyer.',
      body: 'A lawyer can check your eligibility, prepare your application, and flag rejection risks before you submit.',
    },
    topic_taxes: {
      title: 'Tax rules are nuanced — let an accountant check yours.',
      body: 'A local tax accountant can map your income to the actual rates and tax treaties that apply to your situation.',
    },
    'topic_cost-of-living': {
      title: 'Want a realistic budget for your situation?',
      body: 'A relocation consultant who lives there can give you honest numbers — not country averages.',
    },
    topic_property: {
      title: 'Buying or renting? Get a real estate lawyer.',
      body: 'Purchase process, notary, taxes, pitfalls — a lawyer protects you from the expensive surprises.',
    },
    topic_climate: {
      title: 'Need help picking the right region?',
      body: 'A consultant who lives there can tell you which areas actually match your climate and lifestyle preferences.',
    },
    topic_healthcare: {
      title: 'Health insurance is the part most expats get wrong.',
      body: 'A broker can find a plan that covers you properly — public registration, private add-ons, dental, family.',
    },
    topic_education: {
      title: 'Schools for kids? Talk to an education consultant.',
      body: 'International school availability, language tracks, and admission timing vary more than articles can capture.',
    },
    topic_banking: {
      title: 'Opening an account as a foreigner can take weeks.',
      body: 'A banking advisor knows which banks open accounts for your passport and exactly what documents to bring.',
    },
    topic_transport: {
      title: 'License exchange, car registration — get someone who has done it.',
      body: 'A local fixer can handle the paperwork and save you weeks of dealing with bureaucracy.',
    },
    'topic_safety-community': {
      title: 'Pick the right neighbourhood with someone who lives there.',
      body: 'A relocation consultant gives you an honest safety and expat-community picture beyond what statistics show.',
    },
    'topic_language-culture': {
      title: 'Find a tutor or cultural integration coach.',
      body: 'Language and cultural integration are the biggest predictors of long-term happiness abroad. Start early.',
    },
    topic_infrastructure: {
      title: 'Internet, utilities, paperwork — get a local fixer.',
      body: 'A relocation coordinator knows the provider landscape and can set up your apartment from day one.',
    },
    checklist_strategy: {
      title: 'Pick the right visa and tax stance with a pro.',
      body: 'An immigration lawyer and tax advisor can set the right strategy before you commit to a country.',
    },
    checklist_prep: {
      title: 'Documents, translations, notary — get someone to drive this.',
      body: 'A relocation consultant or immigration lawyer can manage the document pipeline so nothing gets missed.',
    },
    checklist_logistics: {
      title: 'Apostilles, shipping, school applications — full turnkey help.',
      body: 'A relocation coordinator can run your move logistics end-to-end.',
    },
    checklist_arrival: {
      title: 'First 2 weeks: account, registration, doctor.',
      body: 'A banking advisor and health insurance broker can get you set up before and after you arrive.',
    },
    checklist_settle: {
      title: 'Find an ongoing accountant for filings and residency.',
      body: 'A local tax accountant handles the ongoing compliance so you can focus on settling in.',
    },
  };
  const fallback = DEFAULTS[source] ?? {
    title: 'Want expert guidance?',
    body: 'Talk to a relocation consultant who knows this topic.',
  };
  const title = t(titleKey, { defaultValue: fallback.title });
  const body = t(bodyKey, { defaultValue: fallback.body });
  const ctaLabel = t('consultantCta.button', { defaultValue: 'Talk to a consultant' });
  const qp = new URLSearchParams({ from: source });
  if (country) qp.set('country', country.toUpperCase());
  if (specialty) qp.set('specialty', specialty);
  const href = `/consultant?${qp.toString()}`;

  if (variant === 'inline') {
    return (
      <div
        data-testid={`consultant-cta-${source}`}
        className={cn(
          'flex flex-col items-start gap-2 rounded-md border border-ink-100 bg-paper-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between',
          className,
        )}
      >
        <div className="min-w-0">
          <p className="font-display text-sm italic text-ink-900">{title}</p>
          <p className="font-mono text-[11px] text-ink-500">{body}</p>
        </div>
        <Link
          to={href}
          className="shrink-0 rounded-md bg-terra-600 px-3 py-1.5 font-mono text-xs font-medium text-paper hover:bg-terra-700"
        >
          {ctaLabel} →
        </Link>
      </div>
    );
  }

  return (
    <aside
      data-testid={`consultant-cta-${source}`}
      className={cn(
        'rounded-lg border border-ink-100 bg-paper-2 px-5 py-5',
        className,
      )}
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-500">
        {t('consultantCta.eyebrow', { defaultValue: 'Human help' })}
      </p>
      <p className="mt-1 font-display text-lg italic text-ink-900">{title}</p>
      <p className="mt-2 text-sm text-ink-700">{body}</p>
      <Link
        to={href}
        className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-terra-600 px-4 py-2 font-mono text-xs font-medium uppercase tracking-wider text-paper hover:bg-terra-700"
      >
        {ctaLabel}
        <span aria-hidden="true">→</span>
      </Link>
    </aside>
  );
}
